'use client';

import { useEffect, useState, useCallback } from 'react';
import Image from 'next/image';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent } from '@/shared/components/ui/dialog';
import { Loader2 } from 'lucide-react';

interface PaintingPrompt {
  id: string;
  imageUrl: string;
  prompt: string;
  width: number;
  height: number;
  aspectRatio?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export default function PaintingPromptGallery() {
  const [prompts, setPrompts] = useState<PaintingPrompt[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 12,
    total: 0,
    pages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<PaintingPrompt | null>(null);

  const fetchPrompts = useCallback(async (page: number) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/painting-prompts?page=${page}&limit=12`);
      const result = await response.json();

      if (result.data) {
        setPrompts(result.data.prompts || []);
        setPagination(result.data.pagination);
      } else {
        console.error('Invalid response structure:', result);
      }
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrompts(1);
  }, [fetchPrompts]);

  const handlePageChange = (newPage: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchPrompts(newPage);
  };

  if (loading && prompts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  if (prompts.length === 0 && !loading) {
    return (
      <div className="text-center py-10">
        <p className="text-muted-foreground">No painting prompts available yet.</p>
      </div>
    );
  }

  return (
    <>
      {/* Gallery Grid */}
      <div className="columns-1 gap-4 sm:columns-2 md:columns-3 lg:columns-4 px-4 py-6">
        {prompts.map((item) => (
          <GalleryItem
            key={item.id}
            item={item}
            onSelect={setSelectedPrompt}
          />
        ))}
      </div>

      {/* Pagination Controls */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8 pb-8 flex-wrap">
          <Button
            variant="outline"
            disabled={pagination.page === 1 || loading}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            Previous
          </Button>

          <div className="flex items-center gap-2 flex-wrap">
            {pagination.pages <= 7 ? (
              // 页数少于等于 7，显示所有页码
              Array.from({ length: pagination.pages }, (_, i) => i + 1).map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pageNum === pagination.page ? 'default' : 'outline'}
                  size="sm"
                  disabled={loading}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </Button>
              ))
            ) : (
              // 页数大于 7，智能显示
              <>
                {/* 第一页 */}
                <Button
                  variant={1 === pagination.page ? 'default' : 'outline'}
                  size="sm"
                  disabled={loading}
                  onClick={() => handlePageChange(1)}
                >
                  1
                </Button>

                {/* 省略号（如果需要） */}
                {pagination.page > 3 && (
                  <span className="text-muted-foreground">...</span>
                )}

                {/* 当前页附近的页码 */}
                {Array.from(
                  {
                    length: Math.min(
                      5,
                      pagination.pages - 2,
                      Math.max(0, pagination.page - 2) + Math.max(0, pagination.pages - pagination.page - 1)
                    )
                  },
                  (_, i) => {
                    const start = Math.max(2, pagination.page - 2);
                    const end = Math.min(pagination.pages - 1, pagination.page + 2);
                    return start + i;
                  }
                )
                  .filter((p) => p >= 2 && p < pagination.pages)
                  .map((pageNum) => (
                    <Button
                      key={pageNum}
                      variant={pageNum === pagination.page ? 'default' : 'outline'}
                      size="sm"
                      disabled={loading}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  ))}

                {/* 省略号（如果需要） */}
                {pagination.page < pagination.pages - 2 && (
                  <span className="text-muted-foreground">...</span>
                )}

                {/* 最后一页 */}
                <Button
                  variant={pagination.pages === pagination.page ? 'default' : 'outline'}
                  size="sm"
                  disabled={loading}
                  onClick={() => handlePageChange(pagination.pages)}
                >
                  {pagination.pages}
                </Button>
              </>
            )}
          </div>

          <Button
            variant="outline"
            disabled={pagination.page === pagination.pages || loading}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Detail Modal */}
      {selectedPrompt && (
        <PromptDetailModal
          prompt={selectedPrompt}
          open={!!selectedPrompt}
          onOpenChange={() => setSelectedPrompt(null)}
        />
      )}
    </>
  );
}

function GalleryItem({
  item,
  onSelect
}: {
  item: PaintingPrompt;
  onSelect: (prompt: PaintingPrompt) => void;
}) {
  return (
    <div className="break-inside-avoid rounded-xl bg-card border shadow-sm transition-all hover:shadow-md mb-4 overflow-hidden cursor-pointer" onClick={() => onSelect(item)}>
      <div className="relative group">
        <Image
          src={item.imageUrl}
          alt={item.prompt}
          width={item.width}
          height={item.height}
          className="w-full h-auto object-cover transition-transform group-hover:scale-105"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <span className="text-white font-medium bg-black/50 px-3 py-1 rounded-full text-sm">
            View Details
          </span>
        </div>
      </div>

      <div className="p-5">
        <p className="line-clamp-4 text-sm text-foreground/80 leading-relaxed mb-0 whitespace-pre-line">
          {item.prompt}
        </p>
      </div>
    </div>
  );
}

function PromptDetailModal({
  prompt,
  open,
  onOpenChange
}: {
  prompt: PaintingPrompt;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(prompt.prompt);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-full p-0 overflow-hidden bg-background">
        <div className="grid md:grid-cols-2 h-full max-h-[80vh]">
          {/* Image Section */}
          <div className="bg-black/5 flex items-center justify-center p-4">
            <Image
              src={prompt.imageUrl}
              alt={prompt.prompt}
              width={prompt.width}
              height={prompt.height}
              className="max-w-full max-h-[70vh] object-contain"
            />
          </div>

          {/* Content Section */}
          <div className="p-6 overflow-y-auto flex flex-col">
            <h3 className="text-lg font-semibold mb-4">Prompt</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-7 flex-1">
              {prompt.prompt}
            </p>

            {/* Metadata */}
            <div className="mt-6 space-y-3 border-t pt-4">
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Size:</span> {prompt.width} x {prompt.height}
              </div>
              {prompt.aspectRatio && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Aspect Ratio:</span> {prompt.aspectRatio}
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Created:</span>{' '}
                {new Date(prompt.createdAt).toLocaleDateString()}
              </div>
            </div>

            {/* Copy Button */}
            <Button
              onClick={handleCopyPrompt}
              className="w-full mt-4"
              variant="default"
            >
              Copy Prompt
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
